<template>
  <div id="caixaVendasOnline">
    <div class="row items-center q-mb-md">
      <div class="col text-subtitle1 text-weight-bold">Vendas Online deste caixa</div>
      <div class="col-auto text-grey-7" data-test="caixa-vendas-a-receber">
        A receber: R$ {{ $currency(aReceber.valor) }} · {{ aReceber.abertas }} {{ aReceber.abertas === 1 ? "venda" : "vendas" }}
      </div>
      <div v-if="transferiveis.length" class="col-auto q-ml-md">
        <q-btn
          unelevated
          no-caps
          color="primary"
          icon="swap_horiz"
          :label="selecionadas.length ? `Transferir (${selecionadas.length})` : 'Transferir'"
          :disable="!selecionadas.length"
          data-test="transferir-selecionadas"
          @click="openTransferencia(vendasSelecionadas)"
        >
          <q-tooltip>Transferir as vendas marcadas para outro caixa aberto</q-tooltip>
        </q-btn>
      </div>
    </div>

    <q-banner v-if="abertasOcultas" dense class="bg-orange-1 text-orange-10 q-mb-md" data-test="caixa-vendas-ocultas">{{ avisoOcultas }}</q-banner>
    <q-banner v-if="erro" dense class="bg-red-1 text-negative q-mb-md" data-test="caixa-vendas-erro">{{ erro }}</q-banner>
    <q-banner v-else-if="limite" dense class="bg-orange-1 text-orange-10 q-mb-md" data-test="caixa-vendas-limite">
      Mostrando as {{ limite }} vendas mais recentes deste caixa. O A receber soma só essas vendas.
    </q-banner>
    <div v-else-if="carregado && !vendas.length" class="text-grey-6" data-test="caixa-vendas-vazio">Nenhuma venda online neste caixa.</div>

    <q-markup-table v-else-if="vendas.length" flat bordered dense separator="horizontal">
      <thead>
        <tr>
          <th v-if="transferiveis.length" class="text-left" style="width: 40px">
            <q-checkbox
              dense
              :model-value="todasSelecionadas"
              data-test="selecionar-todas"
              @update:model-value="selectAll"
            />
          </th>
          <th class="text-left">Status</th>
          <th class="text-left">Venda</th>
          <th class="text-left">Cliente</th>
          <th class="text-right">Valor</th>
          <th class="text-left">Vale até</th>
          <th class="text-right">Ações</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="venda in vendas" :key="venda.id" :data-test="`venda-${venda.codigo}`">
          <td v-if="transferiveis.length">
            <q-checkbox v-if="isTransferivel(venda)" v-model="selecionadas" dense :val="venda.id" :data-test="`selecionar-${venda.codigo}`" />
          </td>
          <td>
            <q-chip dense square text-color="white" :color="getVendaOnlineStatus(venda.status).cor" :icon="getVendaOnlineStatus(venda.status).icone">
              {{ getVendaOnlineStatus(venda.status).rotulo }}
            </q-chip>
            <q-chip v-if="venda.conflito_pendente" dense square color="red-7" text-color="white" icon="report">Conflito</q-chip>
            <contagem-cancelamento v-if="venda.status === 'cancelando'" :prazo="venda.cancelamento_prazo_em" />
          </td>
          <td>{{ venda.codigo }}</td>
          <td>{{ venda.cliente_nome }}</td>
          <td class="text-right">R$ {{ $currency(venda.valor_total) }}</td>
          <td>{{ formatMomento(venda.corte_em) }}</td>
          <td class="text-right">
            <q-btn
              v-if="venda.status === 'ativa'"
              flat
              dense
              round
              icon="content_copy"
              color="primary"
              :data-test="`copiar-link-${venda.codigo}`"
              @click="copyLink(venda)"
            >
              <q-tooltip>Copiar link</q-tooltip>
            </q-btn>
            <q-btn
              v-if="venda.status === 'ativa' && hasVendaOnlineCriarPermissao"
              flat
              dense
              round
              icon="send"
              color="primary"
              :loading="reenviando === venda.id"
              :data-test="`reenviar-link-${venda.codigo}`"
              @click="resend(venda)"
            >
              <q-tooltip>Reenviar link</q-tooltip>
            </q-btn>
            <q-btn
              v-if="isTransferivel(venda)"
              flat
              dense
              round
              icon="swap_horiz"
              color="primary"
              :data-test="`transferir-venda-${venda.codigo}`"
              @click="openTransferencia([venda])"
            >
              <q-tooltip>Transferir para outro caixa</q-tooltip>
            </q-btn>
            <q-btn
              v-if="isCancelavel(venda)"
              flat
              dense
              round
              icon="block"
              color="negative"
              :data-test="`cancelar-venda-${venda.codigo}`"
              @click="openCancelamento(venda)"
            >
              <q-tooltip>Cancelar venda</q-tooltip>
            </q-btn>
          </td>
        </tr>
      </tbody>
    </q-markup-table>

    <modal-transferir-vendas-online
      v-if="transferenciaAberta"
      v-model="transferenciaAberta"
      :caixa-id="caixaId"
      :vendas="alvosTransferencia"
      @transferidas="afterTransferencia"
    />

    <q-dialog v-model="cancelamentoAberto" persistent>
      <q-card style="min-width: 360px" data-test="dialogo-cancelar">
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">Cancelar venda {{ alvo && alvo.codigo }}</div>
          <q-space />
          <q-btn icon="close" flat round dense :disable="cancelando" data-test="fechar-cancelamento" @click="cancelamentoAberto = false" />
        </q-card-section>
        <q-card-section>
          <div class="q-mb-sm">O cancelamento não pode ser desfeito. Se o Pix já tiver sido pago, a venda fica paga e não é cancelada.</div>
          <q-input v-model="motivo" type="textarea" autogrow outlined dense label="Motivo" :maxlength="MOTIVO_MAX" data-test="motivo-cancelamento" />
        </q-card-section>
        <q-card-actions align="right">
          <q-btn
            unelevated
            no-caps
            label="Cancelar venda"
            color="negative"
            :loading="cancelando"
            :disable="!motivo.trim()"
            data-test="confirmar-cancelamento"
            @click="cancel"
          />
        </q-card-actions>
      </q-card>
    </q-dialog>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { copyToClipboard } from "quasar";
import { VENDA_ONLINE_ABERTAS, VENDA_ONLINE_CANCELAVEIS, formatMomento, getVendaOnlineStatus, mensagemAbertasOcultas } from "@/utils/venda-online";
import ContagemCancelamento from "@/components/VendasOnline/contagem-cancelamento.vue";
import ModalTransferirVendasOnline from "@/components/VendasOnline/modal-transferir-vendas-online.vue";

const REFRESH_MS = 15000;

export default {
  name: "caixa-vendas-online",
  components: { ContagemCancelamento, ModalTransferirVendasOnline },
  data() {
    return {
      vendas: [],
      paging: null,
      abertasOcultas: 0,
      carregado: false,
      erro: null,
      reenviando: null,
      cancelamentoAberto: false,
      cancelando: false,
      alvo: null,
      motivo: "",
      transferenciaAberta: false,
      alvosTransferencia: [],
      selecionadas: [],
      timer: null,
      pedido: 0,
      MOTIVO_MAX: 255
    };
  },
  computed: {
    avisoOcultas() {
      return mensagemAbertasOcultas(this.abertasOcultas);
    },
    ...mapGetters(["hasVendaOnlineCriarPermissao"]),
    caixaId() {
      return this.$route.params.id;
    },
    limite() {
      const paging = this.paging || {};

      return paging.limit && paging.count >= paging.limit ? paging.limit : null;
    },
    transferiveis() {
      return this.vendas.filter((venda) => this.isTransferivel(venda));
    },
    vendasSelecionadas() {
      return this.transferiveis.filter((venda) => this.selecionadas.includes(venda.id));
    },
    todasSelecionadas() {
      if (!this.selecionadas.length) return false;

      return this.selecionadas.length === this.transferiveis.length ? true : null;
    },
    aReceber() {
      const abertas = this.vendas.filter((venda) => VENDA_ONLINE_ABERTAS.includes(venda.status));

      return { abertas: abertas.length, valor: abertas.reduce((soma, venda) => soma + Number(venda.valor_total), 0) };
    }
  },
  mounted() {
    this.load();
    this.timer = setInterval(this.load, REFRESH_MS);
  },
  beforeUnmount() {
    clearInterval(this.timer);
  },
  methods: {
    getVendaOnlineStatus,
    formatMomento,
    isTransferivel(venda) {
      return this.hasVendaOnlineCriarPermissao && VENDA_ONLINE_ABERTAS.includes(venda.status);
    },
    selectAll(marcar) {
      this.selecionadas = marcar ? this.transferiveis.map((venda) => venda.id) : [];
    },
    openTransferencia(vendas) {
      this.alvosTransferencia = vendas;
      this.transferenciaAberta = true;
    },
    async afterTransferencia() {
      this.selecionadas = [];
      await this.load();
    },
    isCancelavel(venda) {
      return this.hasVendaOnlineCriarPermissao && VENDA_ONLINE_CANCELAVEIS.includes(venda.status);
    },
    async load() {
      const pedido = ++this.pedido;
      const resultado = await this.$store.dispatch("fetchVendasOnlineCaixa", this.caixaId);
      if (pedido !== this.pedido) return;
      this.carregado = true;
      this.erro = resultado.ok ? null : resultado.mensagem;
      if (!resultado.ok) return;

      this.vendas = resultado.vendas;
      this.paging = resultado.paging;
      this.abertasOcultas = resultado.abertasOcultas || 0;
      const ids = this.transferiveis.map((venda) => venda.id);
      this.selecionadas = this.selecionadas.filter((id) => ids.includes(id));
    },
    notify(message, color) {
      this.$q.notify({ message, color, position: "top-right" });
    },
    async copyLink(venda) {
      const resultado = await this.$store.dispatch("fetchVendaOnline", venda.id);
      if (!resultado.ok) {
        this.notify(resultado.mensagem, "negative");
        return;
      }
      if (!resultado.venda.link_url) {
        this.notify("Esta venda não tem link para copiar.", "orange");
        return;
      }

      try {
        await copyToClipboard(resultado.venda.link_url);
        this.notify("Link copiado", "green");
      } catch (e) {
        this.notify("Não foi possível copiar.", "negative");
      }
    },
    async resend(venda) {
      this.reenviando = venda.id;
      let resultado;
      try {
        resultado = await this.$store.dispatch("resendVendaOnlineLink", venda.id);
      } finally {
        this.reenviando = null;
      }

      this.notify(resultado.mensagem, resultado.ok ? "green" : "negative");
      if (resultado.ok) await this.load();
    },
    openCancelamento(venda) {
      this.alvo = venda;
      this.motivo = "";
      this.cancelamentoAberto = true;
    },
    async cancel() {
      const motivo = this.motivo.trim();
      if (!motivo || this.cancelando) return;

      this.cancelando = true;
      let resultado;
      try {
        resultado = await this.$store.dispatch("cancelVendaOnline", { id: this.alvo.id, motivo });
      } finally {
        this.cancelando = false;
      }

      this.cancelamentoAberto = false;
      if (!resultado.ok) {
        this.notify(resultado.mensagem, "negative");
      } else {
        this.notify(resultado.mensagem, resultado.venda.status === "cancelada" ? "green" : "orange");
      }
      await this.load();
    }
  }
};
</script>
